import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { QuestionComponentProps } from '../../types';
import { CommentField } from '../CommentField';

export const NumberQuestion: React.FC<QuestionComponentProps> = ({
  question,
  value,
  comment,
  language,
  onChange,
  onCommentChange,
  disabled = false,
  error,
  autoFocus = false,
}) => {
  const [localValue, setLocalValue] = useState<string>((value as string) || '');

  const getLocalizedText = (textRecord: Record<string, string>): string => {
    return textRecord[language] || textRecord['en'] || textRecord[Object.keys(textRecord)[0]] || '';
  };

  const getPlaceholderText = (): string => {
    return language === 'no' ? 'Skriv inn et tall' : 'Enter a number';
  };

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue((value as string) || '');
  }, [value]);

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Allow empty, digits, and decimal point
    if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
      setLocalValue(newValue);
      onChange(newValue ? parseFloat(newValue) : undefined);
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 3 }}
    >
      <Typography variant="h5" sx={{ textAlign: 'center', mb: 2 }}>
        {getLocalizedText(question.title)}
      </Typography>

      {question.description && (
        <Typography sx={{ textAlign: 'center', mb: 4, maxWidth: 500, color: 'text.secondary' }}>
          {getLocalizedText(question.description)}
        </Typography>
      )}

      <Box sx={{ width: '100%', maxWidth: 300 }}>
        <TextField
          fullWidth
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleNumberChange}
          placeholder={getPlaceholderText()}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional: focus input for keyboard entry in survey flow
          autoFocus={autoFocus}
          error={!!error}
          inputProps={{
            'aria-label': getLocalizedText(question.title),
          }}
          sx={{
            bgcolor: 'background.paper',
          }}
          data-testid="number-input"
        />
      </Box>

      {question.hasCommentField && onCommentChange && (
        <Box sx={{ maxWidth: 500, width: '100%' }}>
          <CommentField
            value={comment}
            onChange={onCommentChange}
            language={language}
            disabled={disabled}
            placeholder={
              question.commentFieldDescription
                ? getLocalizedText(question.commentFieldDescription)
                : undefined
            }
          />
        </Box>
      )}

      {error && (
        <Typography
          color="error"
          variant="body2"
          sx={{ textAlign: 'center', mt: 2 }}
          role="alert"
          data-testid="error-message"
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
